import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, Utensils, Bike, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RatingModalItem {
  name: string;
  quantity: number;
}

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  restaurantId: string;
  driverId?: string;
  customerId: string;
  restaurantName?: string;
  restaurantImage?: string | null;
  items?: RatingModalItem[];
}

const RatingModal = ({
  isOpen,
  onClose,
  orderId,
  restaurantId,
  driverId,
  customerId,
  restaurantName,
  restaurantImage,
  items = [],
}: RatingModalProps) => {
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [driverRating, setDriverRating] = useState(0);
  const [restaurantComment, setRestaurantComment] = useState('');
  const [driverComment, setDriverComment] = useState('');
  const [hoverRest, setHoverRest] = useState(0);
  const [hoverDriver, setHoverDriver] = useState(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (restaurantRating === 0) {
      toast({
        title: 'Rating required',
        description: 'Please tap a star to rate the restaurant.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('ratings').insert({
        order_id: orderId,
        customer_id: customerId,
        restaurant_id: restaurantId,
        driver_id: driverId || null,
        restaurant_rating: restaurantRating,
        restaurant_comment: restaurantComment || null,
        driver_rating: driverId ? driverRating || null : null,
        driver_comment: driverId && driverComment ? driverComment : null,
      });
      if (error) throw error;

      toast({ title: 'Thanks for your feedback!', description: 'Your rating has been submitted.' });
      onClose();
    } catch (err) {
      console.error('Error submitting rating:', err);
      toast({
        title: 'Error',
        description: 'Failed to submit rating. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const ratingLabels = ['', 'Poor', 'Okay', 'Good', 'Great', 'Amazing'];

  const StarRow = ({
    value,
    hover,
    onHover,
    onPick,
  }: {
    value: number;
    hover: number;
    onHover: (n: number) => void;
    onPick: (n: number) => void;
  }) => (
    <div className="flex items-center justify-center gap-2" onMouseLeave={() => onHover(0)}>
      {[1, 2, 3, 4, 5].map((s) => {
        const active = s <= (hover || value);
        return (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            onMouseEnter={() => onHover(s)}
            className="p-1 transition-transform active:scale-90 hover:scale-110 focus:outline-none"
          >
            <Star
              className={`h-9 w-9 transition-colors ${
                active ? 'fill-yellow-400 text-yellow-400 drop-shadow-sm' : 'text-gray-200'
              }`}
            />
          </button>
        );
      })}
    </div>
  );

  const restaurantInitial = (restaurantName || 'R').charAt(0).toUpperCase();

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-3xl border-0 max-h-[92vh] flex flex-col gap-0">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-rose-600 px-6 pt-7 pb-12 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <h2 className="text-xl font-bold text-center">How was your order?</h2>
          <p className="text-center text-sm text-white/80 mt-1">
            Your feedback helps us serve you better
          </p>
        </div>

        {/* Restaurant card (overlapping) */}
        <div className="px-5 -mt-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center gap-3">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {restaurantImage ? (
                <img src={restaurantImage} alt={restaurantName || 'Restaurant'} className="h-full w-full object-cover" />
              ) : (
                <span className="text-xl font-bold text-red-600">{restaurantInitial}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
                <Utensils className="h-3 w-3" />
                <span>You ordered from</span>
              </div>
              <p className="font-semibold text-gray-900 truncate">{restaurantName || 'Restaurant'}</p>
              {items.length > 0 && (
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {items
                    .slice(0, 3)
                    .map((i) => `${i.quantity}× ${i.name}`)
                    .join(' • ')}
                  {items.length > 3 ? ` +${items.length - 3} more` : ''}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pt-5 pb-2 space-y-5">
          <div>
            <p className="text-center text-sm font-medium text-gray-700 mb-2">
              Rate the restaurant
            </p>
            <StarRow
              value={restaurantRating}
              hover={hoverRest}
              onHover={setHoverRest}
              onPick={setRestaurantRating}
            />
            <p className="text-center text-xs text-gray-500 mt-1 h-4">
              {ratingLabels[hoverRest || restaurantRating]}
            </p>
            <Textarea
              value={restaurantComment}
              onChange={(e) => setRestaurantComment(e.target.value)}
              placeholder="Add a note (optional)"
              className="mt-2 min-h-16 resize-none rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-red-500"
            />
          </div>

          {driverId && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <Bike className="h-4 w-4" />
                <span>Rate your driver</span>
              </div>
              <StarRow
                value={driverRating}
                hover={hoverDriver}
                onHover={setHoverDriver}
                onPick={setDriverRating}
              />
              <p className="text-center text-xs text-gray-500 mt-1 h-4">
                {ratingLabels[hoverDriver || driverRating]}
              </p>
              <Textarea
                value={driverComment}
                onChange={(e) => setDriverComment(e.target.value)}
                placeholder="How was the delivery? (optional)"
                className="mt-2 min-h-16 resize-none rounded-xl bg-gray-50 border-gray-200 focus-visible:ring-red-500"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-white flex gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            className="flex-1 rounded-xl text-gray-600 hover:bg-gray-100"
          >
            Maybe later
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || restaurantRating === 0}
            className="flex-[1.5] rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold shadow-md shadow-red-600/30 disabled:opacity-50 disabled:shadow-none"
          >
            {loading ? 'Submitting...' : 'Submit rating'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RatingModal;
